import React, { useState, useEffect } from 'react'

function fmt(n) {
  n = Math.round(n)
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr'
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + ' L'
  if (n >= 1000) return '₹' + n.toLocaleString('en-IN')
  return '₹' + n
}

function ord(n) {
  n = parseInt(n)
  const s = (n === 1 || n === 21 || n === 31) ? 'st' : (n === 2 || n === 22) ? 'nd' : (n === 3 || n === 23) ? 'rd' : 'th'
  return n + s
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function Reminders({ loans, emiPayments, currentMonth }) {
  const [permission, setPermission] = useState('default')
  const [timings, setTimings] = useState(() => {
    try { return JSON.parse(localStorage.getItem('da_rtimings') || '[0,1,3]') } catch { return [0,1,3] }
  })

  const now = new Date()
  const todayDate = now.getDate()

  useEffect(() => {
    if ('Notification' in window) setPermission(Notification.permission)
  }, [])

  function saveTimings(t) {
    setTimings(t)
    localStorage.setItem('da_rtimings', JSON.stringify(t))
  }

  function toggleTiming(days) {
    const idx = timings.indexOf(days)
    const next = idx === -1 ? [...timings, days] : timings.filter(d => d !== days)
    saveTimings(next)
  }

  async function enableNotifications() {
    if (!('Notification' in window)) { alert('Your browser does not support notifications'); return }
    const perm = await Notification.requestPermission()
    setPermission(perm)
    if (perm === 'granted') {
      scheduleReminders()
    }
  }

  function sendTest() {
    if (permission !== 'granted') { alert('Please enable notifications first'); return }
    new Notification('🔔 DebtCrush — Test Reminder', {
      body: 'Your EMI reminders are working! You will be notified before each due date.',
    })
  }

  function scheduleReminders() {
    if (permission !== 'granted') return
    loans.forEach(loan => {
      if (!loan.due_day) return
      const thisMonthPayment = emiPayments.find(p => p.loan_id === loan.id && p.payment_month === currentMonth)
      if (thisMonthPayment?.status === 'paid' || thisMonthPayment?.status === 'na') return

      timings.forEach(daysBefore => {
        const dueDay = parseInt(loan.due_day)
        const reminderDay = dueDay - daysBefore
        if (reminderDay !== todayDate) return

        let title, body
        if (daysBefore === 0) {
          title = '🔴 EMI Due Today!'
          body = `${loan.name} EMI of ${fmt(loan.emi_amount)} is due today (${ord(dueDay)}). Pay now!`
        } else if (daysBefore === 1) {
          title = '🟠 EMI Due Tomorrow!'
          body = `${loan.name} EMI of ${fmt(loan.emi_amount)} is due tomorrow. Get ready!`
        } else {
          title = `🟡 Upcoming EMI in ${daysBefore} days`
          body = `${loan.name} EMI of ${fmt(loan.emi_amount)} is due on ${ord(dueDay)}. Plan your payment.`
        }

        setTimeout(() => {
          new Notification(title, { body, tag: `emi-${loan.id}-${daysBefore}` })
        }, Math.random() * 2000)
      })
    })
  }

  // Upcoming EMIs
  const withDue = loans.filter(l => l.due_day)
  const thisMonthData = {}
  emiPayments.filter(p => p.payment_month === currentMonth).forEach(p => { thisMonthData[p.loan_id] = p.status })

  function getUrgency(loan) {
    const st = thisMonthData[loan.id]
    if (st === 'paid') return { label: '✓ Paid', color: '#4a6741', bg: 'rgba(74,103,65,0.1)' }
    const diff = parseInt(loan.due_day) - todayDate
    if (diff < 0) return { label: `⚠️ Overdue by ${Math.abs(diff)} day${Math.abs(diff) !== 1 ? 's' : ''}`, color: '#c0392b', bg: 'rgba(192,57,43,0.1)' }
    if (diff === 0) return { label: '🔴 Due Today!', color: '#c0392b', bg: 'rgba(192,57,43,0.08)', fontWeight: '700' }
    if (diff === 1) return { label: '🟠 Due Tomorrow', color: '#b8441a', bg: 'rgba(184,68,26,0.08)' }
    if (diff <= 3) return { label: `🟡 Due in ${diff} days`, color: '#7a5d1e', bg: 'rgba(201,168,76,0.1)' }
    return { label: `Due ${ord(loan.due_day)}`, color: 'var(--text-muted)', bg: 'var(--surface-hdr)' }
  }

  // Banner config
  const bannerConfig = {
    granted: { bg: 'rgba(74,103,65,0.06)', border: '#4a6741', text: '✅ Notifications are enabled. You will receive EMI reminders based on your timing settings.', btnLabel: '✅ Notifications On', btnDisabled: true },
    denied: { bg: 'rgba(184,68,26,0.06)', border: '#b8441a', text: '❌ Notifications are blocked. Please click the lock icon in your browser address bar and allow notifications for this site.', btnLabel: '🔔 Blocked — Enable in Browser', btnDisabled: true },
    default: { bg: 'rgba(201,168,76,0.08)', border: '#c9a84c', text: '🔔 Enable browser notifications to get EMI reminders before your due dates.', btnLabel: '🔔 Enable Notifications', btnDisabled: false },
  }
  const bc = bannerConfig[permission] || bannerConfig.default

  const panelStyle = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginBottom: '1.5rem' }
  const hdrStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }

  return (
    <div>
      <div style={{ marginBottom: '1.4rem' }}>
        <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0 }}>EMI Reminders</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Get browser notifications before your EMI due dates so you never miss a payment.</p>
      </div>

      {/* Settings Panel */}
      <div style={panelStyle}>
        <div style={hdrStyle}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Notification Settings</span>
          <span style={{
            fontFamily: 'monospace', fontSize: '0.68rem',
            color: permission === 'granted' ? '#4a6741' : permission === 'denied' ? '#b8441a' : 'var(--text-muted)'
          }}>
            {permission === 'granted' ? 'ACTIVE' : permission === 'denied' ? 'BLOCKED' : 'NOT ENABLED'}
          </span>
        </div>

        {/* Banner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1.4rem', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', background: bc.bg, borderLeft: `3px solid ${bc.border}` }}>
          <span style={{ flex: 1, fontSize: '0.82rem' }}>{bc.text}</span>
          <button
            onClick={enableNotifications}
            disabled={bc.btnDisabled}
            style={{
              padding: '0.32rem 0.8rem', borderRadius: '4px',
              border: '1px solid var(--border)', fontFamily: 'monospace',
              fontSize: '0.68rem', fontWeight: '500', cursor: bc.btnDisabled ? 'default' : 'pointer',
              background: permission === 'granted' ? '#4a6741' : '#0d0d0d',
              color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em'
            }}
          >
            {bc.btnLabel}
          </button>
        </div>

        {/* Timing toggles */}
        <div style={{ padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.7rem' }}>
            Remind me on:
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {[
              { days: 0, label: '🔴 On due date' },
              { days: 1, label: '🟠 1 day before' },
              { days: 3, label: '🟡 3 days before' },
            ].map(({ days, label }) => (
              <button
                key={days}
                onClick={() => toggleTiming(days)}
                style={{
                  fontFamily: 'monospace', fontSize: '0.63rem',
                  padding: '0.2rem 0.55rem', borderRadius: '3px',
                  border: '1px solid', cursor: 'pointer',
                  borderColor: timings.includes(days) ? '#0d0d0d' : 'var(--border)',
                  background: timings.includes(days) ? '#0d0d0d' : 'transparent',
                  color: timings.includes(days) ? '#f5f0e8' : 'var(--text-muted)',
                  transition: 'all 0.15s'
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Test button */}
        <div style={{ padding: '1rem 1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Test that notifications are working on your device.</span>
          <button
            onClick={sendTest}
            style={{
              background: 'transparent', color: 'var(--text-muted)',
              border: '1px solid var(--border)', padding: '0.5rem 0.8rem',
              borderRadius: '4px', fontFamily: 'monospace',
              fontSize: '0.72rem', cursor: 'pointer'
            }}
          >
            ▶ Send Test Notification
          </button>
        </div>
      </div>

      {/* Upcoming EMIs */}
      <div style={panelStyle}>
        <div style={hdrStyle}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Upcoming EMIs — {MONTHS[now.getMonth()]} {now.getFullYear()}</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{withDue.length} loan{withDue.length !== 1 ? 's' : ''} with due dates</span>
        </div>

        {withDue.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
            <div style={{ fontWeight: '500', marginBottom: '0.3rem' }}>No due dates set</div>
            <div style={{ fontSize: '0.8rem' }}>Edit your loans and add an EMI due day to see reminders here.</div>
          </div>
        ) : (
          withDue.map((loan, i) => {
            const urg = getUrgency(loan)
            const notifDays = timings.map(rb => {
              const rd = parseInt(loan.due_day) - rb
              if (rd < 1) return null
              return (rb === 0 ? 'On due date' : rb === 1 ? '1 day before' : '3 days before') + ` (${ord(rd)})`
            }).filter(Boolean)

            return (
              <div key={loan.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.85rem 1.4rem', gap: '0.5rem',
                borderBottom: i < withDue.length - 1 ? '1px solid var(--border)' : 'none',
                transition: 'background 0.15s'
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: '500', fontSize: '0.85rem' }}>{loan.name}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                    EMI: {fmt(loan.emi_amount)} &nbsp;·&nbsp; Due: {ord(loan.due_day)} every month
                  </div>
                  {notifDays.length > 0 && (
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                      🔔 {notifDays.join('  ·  ')}
                    </div>
                  )}
                </div>
                <span style={{
                  fontFamily: 'monospace', fontSize: '0.62rem',
                  padding: '0.18rem 0.5rem', borderRadius: '3px',
                  background: urg.bg, color: urg.color,
                  fontWeight: urg.fontWeight || '500', whiteSpace: 'nowrap'
                }}>
                  {urg.label}
                </span>
              </div>
            )
          })
        )}
      </div>

      {/* How it works */}
      <div style={panelStyle}>
        <div style={hdrStyle}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>How Reminders Work</span>
        </div>
        <div style={{ padding: '1.2rem 1.4rem', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem' }}>
          {[
            { icon: '🔔', title: 'Enable Once', desc: 'Allow notifications when prompted. This is a one-time step.' },
            { icon: '📅', title: 'Auto Scheduled', desc: 'App checks daily when opened and fires reminders for upcoming EMIs.' },
            { icon: '✅', title: 'Mark as Paid', desc: 'After paying, mark EMI as paid on the Dashboard to stop reminders.' },
          ].map(item => (
            <div key={item.title} style={{ textAlign: 'center', padding: '0.5rem' }}>
              <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>{item.icon}</div>
              <div style={{ fontWeight: '500', fontSize: '0.85rem', marginBottom: '0.3rem' }}>{item.title}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: '0.8rem 1.4rem', borderTop: '1px solid var(--border)', fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <strong>Note:</strong> Browser notifications work when the app is open. Full push notifications (even when app is closed) will be available in the Android app coming soon!
        </div>
      </div>
    </div>
  )
}